import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import { useEffect, useState } from 'react'
import Switch from 'react-switch'
import { useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import useGetTransparency from '../memberTransparency/hooks/useGetTransparency'
import useSetBankerAccess from './hooks/useSetBankerAccess'

const ROWS: Array<{
    key: keyof IBankerSetting
    title: string
    help: string
}> = [
    {
        key: 'banker_can_view_finance',
        title: 'مشاهدهٔ وضعیت مالی صندوق',
        help: 'بانی موجودی صندوق، داشبورد مدیریت، گزارش اکسل و وام‌ها و تراکنش‌های همهٔ اعضا را می‌بیند.',
    },
    {
        key: 'banker_can_record_cashin',
        title: 'ثبت واریز',
        help: 'بانی می‌تواند حق عضویت و قسطی را که خارج از همیان پرداخت شده ثبت کند و ثبت‌های خودش را حذف کند.',
    },
    {
        key: 'banker_can_record_cashout',
        title: 'ثبت برداشت',
        help: 'بانی می‌تواند برداشتی را که خارج از همیان انجام شده ثبت کند؛ واریز به حساب بانکی از همیان فقط کار مدیر است.',
    },
]

/**
 * «دسترسی بانی» — the three switches that decide what a «بانی» (role `bnk`)
 * may do in this fund. The server derives every banker's `capabilities` from
 * them and enforces the same rules on each endpoint.
 */
export default function BankerAccess() {
    const { id } = useParams()
    const { data, isLoading, refetch } = useGetTransparency()
    const server = data?.banker_setting
    const [checked, setChecked] = useState<IBankerSetting | undefined>()

    // Seed from the server once it answers — not at mount: arriving here
    // without a warm cache would otherwise show (and then save) every switch
    // as off.
    useEffect(() => {
        if (server && !checked) setChecked({ ...server })
    }, [server, checked])

    useEffect(() => {
        if (id) posthog.capture('banker_settings_viewed', { fund_id: id })
    }, [id])

    const { mutate, isLoading: isSaving } = useSetBankerAccess(refetch, server)

    return (
        <div className="  flex flex-col bg-white h-full">
            <div className=" grow-0">
                <Header title="تنظیمات صندوق" />
            </div>
            <div className=" grow px-2">
                <p className=" text-lg my-2">دسترسی بانی</p>
                <p className=" text-sm leading-6">
                    بانی عضوی است که کارهای مالی صندوق را انجام می‌دهد ولی مدیر
                    نیست. مشخص کنید بانی‌های این صندوق به چه چیزهایی دسترسی
                    داشته باشند:
                </p>
                <hr className=" my-3" />
                {isLoading || (server && !checked) ? (
                    <DotsLoading />
                ) : !server || !checked ? (
                    <p className=" my-3 px-2 text-sm text-gray-1">
                        تنظیمات بانی در دسترس نیست. کمی بعد دوباره امتحان کنید.
                    </p>
                ) : (
                    ROWS.map((row, index) => (
                        <div key={row.key}>
                            <div className=" w-full flex justify-between items-center my-2 px-2">
                                <p className=" text-lg">{row.title}</p>
                                <Switch
                                    onChange={() =>
                                        setChecked({
                                            ...checked,
                                            [row.key]: !checked[row.key],
                                        })
                                    }
                                    checked={checked[row.key]}
                                    onColor="#75B844"
                                    onHandleColor="#EAEEF0"
                                    handleDiameter={25}
                                    uncheckedIcon={false}
                                    checkedIcon={false}
                                    boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                    activeBoxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                    height={30}
                                    width={60}
                                    className="react-switch"
                                    id={`banker-switch-${row.key}`}
                                    offColor="#B6BCC8"
                                />
                            </div>
                            <p className=" my-3 px-2 text-sm">{row.help}</p>
                            {index < ROWS.length - 1 && (
                                <hr className=" my-2" />
                            )}
                        </div>
                    ))
                )}
                <hr className=" my-3" />
                <p className=" px-2 text-xs leading-5 text-gray-1">
                    برای بانی کردن یک عضو، در «ویرایش اطلاعات اعضا» روی نام او
                    بزنید و «تبدیل به بانی» را انتخاب کنید.
                </p>
            </div>
            <div className=" grow-0">
                <div className=" bg-white py-4 px-4">
                    <button
                        className=" w-full bg-green-1 rounded-2xl text-white py-3 disabled:opacity-60"
                        disabled={!checked || isSaving}
                        onClick={() => {
                            if (!checked) return
                            mutate({ moneyPool: id, ...checked })
                        }}
                    >
                        ثبت تغییرات
                    </button>
                </div>
            </div>
        </div>
    )
}
